import { computed, ComputedRef } from 'vue'
import type { Product } from '~/types/shopware'
import { getProductTranslatedCustomFields } from '~/types/shopware'
import { BookJsonLdData, generateBookJsonLd, BookFormatType, LanguageType, AvailabilityType } from '~/lib/utils'

/**
 * Composable for adding schema.org JSON-LD structured data to book product pages
 * @param product Shopware product object or computed ref to product
 * @param productType The type of the product
 */
export const useSchemaProduct = (
  product: Product | ComputedRef<Product | undefined>,
  productType: ComputedRef<string | undefined>,
) => {
  // Handle product being either a direct object or a computed ref
  const productValue = computed(() => {
    if ((product as ComputedRef<Product | undefined>).value !== undefined) {
      return (product as ComputedRef<Product | undefined>).value
    }
    return product as Product
  })

  const productTypeValue = computed(() => {
    if ((productType as ComputedRef<string | undefined>).value !== undefined) {
      return (productType as ComputedRef<string | undefined>).value
    }
    return productType.value || '' // Ensure productType is a string
  })

  // Only proceed if we have a valid product
  if (!productValue.value) return

  // Extract custom fields safely
  const customFields = getProductTranslatedCustomFields(productValue.value) || {}

  // Determine book format based on product type or custom fields
  let bookFormat: BookFormatType | undefined
  switch (productTypeValue.value) {
    case 'e-book':
      bookFormat = BookFormatType.EBook
      break
    default:
      bookFormat = BookFormatType.Paperback
      break
  }

  // Determine language
  let language: LanguageType | undefined
  const productLanguage = customFields.custom_product_first_language_code || ''
  if (typeof productLanguage === 'string') {
    if (productLanguage.includes('ger')) {
      language = LanguageType.German
    } else if (productLanguage.includes('fre')) {
      language = LanguageType.French
    } else if (productLanguage.includes('ita')) {
      language = LanguageType.Italian
    } else if (productLanguage.includes('eng')) {
      language = LanguageType.English
    }
  }

  // Extract number of pages if available
  let numberOfPages: number | undefined
  if (
    customFields.custom_product_fields_page_count &&
    typeof customFields.custom_product_fields_page_count === 'string'
  ) {
    const parsedPages = parseInt(customFields.custom_product_fields_page_count)
    if (!isNaN(parsedPages)) {
      numberOfPages = parsedPages
    }
  }

  // Create the book data object
  const bookData: BookJsonLdData = {
    name: productValue.value.translated?.name || 'Product',
    author:
      typeof customFields.custom_product_fields_author === 'string'
        ? customFields.custom_product_fields_author
        : undefined,
    bookFormat,
    image: productValue.value?.cover?.media?.url,
    inLanguage: language,
    isbn: productValue.value.ean || undefined,
    numberOfPages,
    datePublished:
      typeof customFields.custom_product_fields_publication_date === 'string'
        ? customFields.custom_product_fields_publication_date
        : undefined,
    offers: {
      price: productValue.value.calculatedPrice?.totalPrice.toString(),
      priceCurrency: 'CHF',
      availability:
        productValue.value.stock && productValue.value.stock > 0
          ? AvailabilityType.InStock
          : AvailabilityType.OutOfStock,
    },
  }

  // Generate the JSON-LD and add it to the page head
  const jsonLd = generateBookJsonLd(bookData)

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(jsonLd),
      },
    ],
  })
}
